import React, { useState, useEffect, useRef, useCallback } from 'react';
import './GlobalSearch.css';

// Site-wide lookup. Paste in whatever identifier you have — order number, serial, tag UID,
// school or district name, someone's name or email, a student ID — and this says where in the
// portal it lives, then takes you there.
//
// Most things live in more than one place, and which one you want depends on what you're asking:
// a serial is a row in the Database, a line on the order that shipped it, and an item on its
// school's Products tab. So a hit offers every page it can be opened on. The first is the default
// — clicking the row still gets you there in one click — and the rest sit alongside it as choices.
//
// The server decides which sections each hit belongs to based on your login's role, and drops
// hits (and individual destinations) whose page you can't open — a warehouse login gets serials
// and orders; sales gets schools, districts and people. So this component never has to reason
// about permissions: it renders whatever came back and trusts hit.sections.

const KIND_ICON = {
  order: '#',
  product: '▣',
  school: '⌂',
  district: '◎',
  user: '☺',
  invite: '✉',
  student: '•',
};

// Where each section lands, for the "found in" hint on every row.
const SECTION_LABEL = {
  schools: 'Schools',
  districts: 'Districts',
  orders: 'Orders',
  database: 'Database',
  serialization: 'Serialization',
  emails: 'Emails',
  tagReports: 'Tag reports',
};

// The destinations one hit can be opened on. Named as concretely as the hit allows: "Riverside
// High" reads as a place you'd go, where a generic "Schools" makes you work out which school it
// meant. Falls back to hit.section so a hit from an older server still renders one destination.
function destinationsFor(hit) {
  const sections = hit.sections && hit.sections.length ? hit.sections : [hit.section];
  return sections.filter(Boolean).map(section => {
    let label = SECTION_LABEL[section] || section;
    if (section === 'schools' && hit.schoolName) label = hit.schoolName;
    else if (section === 'districts' && hit.districtName) label = hit.districtName;
    else if (section === 'orders' && hit.orderNumber) label = `Order ${hit.orderNumber}`;
    return { section, label };
  });
}

function GlobalSearch({ onNavigate }) {
  const [q, setQ] = useState('');
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  // Which destination of the active row is highlighted, for ←/→ selection.
  const [destIdx, setDestIdx] = useState(0);
  const debounce = useRef(null);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // One flat list behind the grouped display, so arrow keys walk the whole result set.
  const flat = groups.flatMap(g => g.hits);

  const runSearch = useCallback(async (term) => {
    if (term.length < 2) {
      setGroups([]);
      setSearched(false);
      return;
    }
    setLoading(true);
    try {
      const api = process.env.REACT_APP_API_ENDPOINT;
      const auth = 'Basic ' + btoa(
        `${localStorage.getItem('admin_username')}:${localStorage.getItem('admin_password')}`
      );
      const res = await fetch(`${api}/admin/search?q=${encodeURIComponent(term)}`, {
        headers: { 'Authorization': auth },
        cache: 'no-store',
      });
      if (res.ok) {
        const data = await res.json();
        setGroups(data.groups || []);
        setActiveIdx(0);
        setDestIdx(0);
        setSearched(true);
      }
    } catch (err) {
      console.error('Global search failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const onChange = (value) => {
    setQ(value);
    setOpen(true);
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => runSearch(value.trim()), 250);
  };

  // Click anywhere outside the search box to dismiss the results.
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Cmd/Ctrl-K focuses search from anywhere in the portal.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Open a hit on a specific page. Everything downstream keys off hit.section, so choosing a
  // destination is just handing the navigator the same hit pointed somewhere else.
  const pick = (hit, section) => {
    setOpen(false);
    inputRef.current?.blur();
    onNavigate(section && section !== hit.section ? { ...hit, section } : hit);
  };

  // Moving to another row starts its choice back at that row's default.
  const moveTo = (nextIdx) => {
    setActiveIdx(nextIdx);
    setDestIdx(0);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur(); return; }
    if (!open || flat.length === 0) return;
    const dests = destinationsFor(flat[activeIdx] || {});
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveTo((activeIdx + 1) % flat.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveTo((activeIdx - 1 + flat.length) % flat.length);
    } else if (e.key === 'ArrowRight' && dests.length > 1) {
      e.preventDefault();
      setDestIdx(i => (i + 1) % dests.length);
    } else if (e.key === 'ArrowLeft' && dests.length > 1) {
      e.preventDefault();
      setDestIdx(i => (i - 1 + dests.length) % dests.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const hit = flat[activeIdx];
      if (hit) pick(hit, (dests[destIdx] || dests[0] || {}).section);
    }
  };

  const clear = () => {
    setQ('');
    setGroups([]);
    setSearched(false);
    setOpen(false);
    setActiveIdx(0);
    setDestIdx(0);
  };

  // Running index across groups, to line the rendered rows up with `flat`.
  let idx = -1;

  return (
    <div className="global-search" ref={wrapRef}>
      <div className="global-search-box">
        <span className="global-search-icon">⌕</span>
        <input
          ref={inputRef}
          className="global-search-input"
          value={q}
          onChange={e => onChange(e.target.value)}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search order #, serial, school, district, person…"
          aria-label="Search the whole portal"
        />
        {q ? (
          <button className="global-search-clear" onClick={clear} title="Clear">×</button>
        ) : (
          <span className="global-search-hint">⌘K</span>
        )}
      </div>

      {open && q.trim().length >= 2 && (
        <div className="global-search-results" role="listbox" aria-label="Search results">
          {loading && flat.length === 0 ? (
            <div className="global-search-empty">Searching…</div>
          ) : flat.length === 0 ? (
            <div className="global-search-empty">
              {searched ? <>Nothing matches “{q.trim()}” in the areas your login can open.</> : 'Searching…'}
            </div>
          ) : (
            groups.map(group => (
              <div className="global-search-group" key={group.kind}>
                <div className="global-search-group-head">
                  <span>{group.label}</span>
                  {group.total > group.hits.length && (
                    <span className="global-search-count">
                      showing {group.hits.length} of {group.total}
                    </span>
                  )}
                </div>
                {group.hits.map(hit => {
                  idx += 1;
                  const myIdx = idx;
                  const isActive = myIdx === activeIdx;
                  const dests = destinationsFor(hit);
                  return (
                    <div
                      key={`${hit.kind}-${hit.id}`}
                      className={`global-search-hit ${isActive ? 'active' : ''}`}
                      role="option"
                      aria-selected={isActive}
                      onMouseEnter={() => moveTo(myIdx)}
                      // Clicking the row itself takes the default page, so the common case stays
                      // one click; the buttons alongside are for when you want the other one.
                      onClick={() => pick(hit, dests[0] && dests[0].section)}
                    >
                      <span className="global-search-hit-icon">{KIND_ICON[hit.kind] || '•'}</span>
                      <span className="global-search-hit-text">
                        <span className="global-search-hit-title">
                          {hit.title}
                          {hit.badge ? <span className="global-search-badge">{hit.badge}</span> : null}
                        </span>
                        <span className="global-search-hit-sub">{hit.subtitle}</span>
                      </span>
                      {dests.length > 1 ? (
                        <span className="global-search-hit-dests">
                          <span className="global-search-dests-label">See in</span>
                          {dests.map((dest, di) => (
                            <button
                              key={dest.section}
                              type="button"
                              className={`global-search-dest ${isActive && di === destIdx ? 'selected' : ''}`}
                              title={`Open in ${dest.label}`}
                              onMouseEnter={() => { moveTo(myIdx); setDestIdx(di); }}
                              onClick={e => { e.stopPropagation(); pick(hit, dest.section); }}
                            >
                              {dest.label}
                            </button>
                          ))}
                        </span>
                      ) : (
                        <span className="global-search-hit-where">
                          {(dests[0] || {}).label || SECTION_LABEL[hit.section] || hit.section}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default GlobalSearch;
